import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { DealerAvailabilityRequests } from "@/components/sourcing/AvailabilityRequests";
import { formatApiErrorDetail } from "@/lib/errors";
import { toast } from "sonner";
import { Loader2, PackagePlus } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { SourcingActions, SourcingJourney } from "@/components/sourcing/SourcingLifecycle";

const STATUS_BADGE = {
  OPEN: "badge-reserved",
  EVALUATING: "badge-reserved",
  PRODUCTION_REQUIRED: "badge-committed",
  QUOTE_REQUIRED: "badge-committed",
  SOURCING_CONFIRMED: "badge-available",
  UNABLE_TO_SOURCE: "badge-sold",
};

export default function DealerSourcing() {
  const [rows, setRows] = useState(null);
  const [params] = useSearchParams();
  const focus = params.get("focus");  // RC49-NOT-002 deep link
  useEffect(() => { if (rows && focus) document.getElementById(`sourcing-${focus}`)?.scrollIntoView({ behavior: "smooth", block: "center" }); }, [rows, focus]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/dealer/sourcing-requests");
      setRows(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!rows) {
    return <div className="flex min-h-[40vh] items-center justify-center" data-testid="dealer-sourcing-loading"><Loader2 className="h-6 w-6 animate-spin text-terracotta" /></div>;
  }

  return (
    <div data-testid="dealer-sourcing-page">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Commercial</p>
      <h1 className="mt-2 font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl">Sourcing requests</h1>
      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-warmgrey">
        Every request is a durable record — you can always see where it stands. Timing is only shown once Zelligio
        has verified it; until then it is honestly marked as requiring confirmation.
      </p>

      {rows.length === 0 ? (
        <div className="mt-8 rounded-md border border-dashed border-limestone bg-white p-10 text-center" data-testid="sourcing-empty">
          <PackagePlus className="mx-auto h-8 w-8 text-warmgrey" />
          <p className="mt-3 text-sm text-warmgrey">No sourcing requests yet. When inventory cannot fulfill a need, use Request sourcing from the Inventory page.</p>
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {rows.map((r) => (
            <div key={r.id} id={`sourcing-${r.id}`} className={`rounded-md border bg-white p-4 ${focus === r.id ? "border-terracotta ring-2 ring-terracotta/30" : "border-border"}`} data-testid={`sourcing-request-${r.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <span className="font-medium text-charcoal">{r.sku}</span>
                  <span className="ml-2 text-sm text-charcoal">{r.color_name}{r.us_color_name ? ` · ${r.us_color_name}` : ""} · {r.format_in}</span>
                  <p className="mt-0.5 text-xs text-warmgrey">{r.boxes} boxes · {r.sqft} sq ft · requested {new Date(r.created_at).toLocaleString()}</p>
                </div>
                <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase ${STATUS_BADGE[r.status] || "badge-reserved"}`}
                  data-testid={`sourcing-status-${r.id}`}>{r.status_label}</span>
              </div>
              <p className="mt-2 text-xs text-warmgrey" data-testid={`sourcing-timing-${r.id}`}>{r.timing_display}</p>
              {r.journey && <SourcingJourney journey={r.journey} id={r.id} />}
              {r.payment_stages?.current_stage && (
                <p className="mt-1 text-xs text-warmgrey" data-testid={`sourcing-payment-stage-${r.id}`}>
                  Payment terms: {r.payment_stages.terms}. Stage: <strong>{r.payment_stages.current_stage.replace(/_/g, " ")}</strong>
                  {r.payment_stages.deposit.recorded ? " · 50% deposit recorded" : " · 50% deposit not yet recorded"}
                  {r.payment_stages.balance.recorded ? " · balance recorded" : ""}
                </p>
              )}
              <SourcingActions r={r} reload={load} />
              {(r.history || []).length > 1 && (
                <ul className="mt-2 space-y-1 border-t border-limestone pt-2">
                  {r.history.map((h, i) => (
                    <li key={i} className="text-[11px] text-warmgrey" data-testid={`sourcing-history-${r.id}-${i}`}>
                      {h.status_label || h.status} · {h.actor} · {new Date(h.at).toLocaleString()}{h.note ? ` — ${h.note}` : ""}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
      <DealerAvailabilityRequests />
    </div>
  );
}
